<template>
  <main class="board-of-regents">
    <Breadcrumb :items="['Home', 'Board of Regents']" /> <!-- Bypass prop -->

    <!-- Hero -->
    <PageHeader
      title="Meeting of the Board of Regents of the University System of Georgia, 2026"
      backgroundImage="board-of-regents/BOR_header-bg.jpg"
      backgroundColor="transparent"
      textColor="white"
      overlay="dark"
      align="center"
      size="lg"
    >
      <img
        :src="require('@/assets/img/board-of-regents/BOR_logo.png')"
        alt=""
        class="bor-hero__logo"
      />
    </PageHeader>

    <!-- Intro / RSVP -->
    <section class="container text-center my-5 py-2">
      <h2>
        University System of Georgia<br />Board of Regents Off-Site Meeting<br />October 5&ndash;7, 2026
      </h2>
    </section>

    <!-- Information -->
    <section class="container text-center my-5 pb-5">
      <h2>Meeting Information</h2>
      <nav class="bor-jump" aria-label="Jump to a section">
        <ul class="bor-jump__list">
          <li v-for="section in sections" :key="section.id">
            <!-- A plain anchor rather than Btn: Btn renders a router-link for
                 non-http hrefs, and the router's scrollBehavior forces every
                 navigation to the top of the page, which would defeat the jump. -->
            <a
              :href="`#${section.id}`"
              class="btn btn-outline-red"
              @click="scrollToSection($event, section.id)"
            >
              {{ section.label }}
            </a>
          </li>
        </ul>
      </nav>
    </section>

    <!-- Schedule of Events -->
    <section id="schedule-of-events" class="container my-5 bor-jump-target">
      <div class="bor-schedule bg-creamery">
        <div class="text-center mb-5">
          <h2 class="mb-0">Schedule of Events</h2>
        </div>

        <!-- Monday -->
        <h3 class="bor-schedule__day">Monday, October 5</h3>
        <ul class="bor-schedule__events">
          <li class="bor-event">
            <p class="bor-event__title">
              <span class="bor-event__time">11:30 a.m.-5 p.m.</span>
              <span class="bor-event__name">USG Presidents Meeting</span>
            </p>
            <ul class="bor-event__details">
              <li>
                <strong>Location:
                <a :href="links.georgiaCenter" v-bind="externalLink">
                  Georgia Center for Continuing Education &amp; Hotel</a>, Rooms K&ndash;L</strong>
              </li>
            </ul>
          </li>
          <li class="bor-event">
            <p class="bor-event__title">
              <span class="bor-event__time">6-8 p.m.</span>
              <span class="bor-event__name">
                Reception and Dinner
              </span>
            </p>
            <ul class="bor-event__details">
              <li>
                <strong>Location: <a :href="links.botanicalGarden" v-bind="externalLink">State Botanical Garden of Georgia</a></strong>
              </li>
              <li>
                <strong>Shuttles:</strong> Continuous round-trip shuttles will begin departing
                from the Georgia Center at 5:30 p.m.
              </li>
              <li>
                <strong>Parking:</strong> Self-parking is available at the State Botanical Garden.
              </li>
            </ul>
            <figure style="margin-top: 1rem;" class="bor-event__figure">
              <img
                :src="require('@/assets/img/board-of-regents/BOR_botanical-gardens.webp')"
                alt="The State Botanical Garden of Georgia at the University of Georgia"
                loading="lazy"
              />
            </figure>
            <p>
              Enjoy an evening that celebrates the connection between art, nature and
              Georgia&rsquo;s rich agricultural heritage at the State Botanical Garden of
              Georgia. Guests will gather for a reception and dinner featuring thoughtfully
              curated food and beverage stations showcasing products from across the state,
              complemented by live entertainment performed by UGA faculty and students.
            </p>
          </li>
        </ul>

        <!-- Tuesday -->
        <h3 class="bor-schedule__day">Tuesday, October 6</h3>
        <ul class="bor-schedule__events">
          <li class="bor-event">
            <p class="bor-event__title">
              <span class="bor-event__time">7-10 a.m.</span>
              <span class="bor-event__name">Breakfast</span>
            </p>
            <figure class="bor-event__figure">
              <img
                :src="require('@/assets/img/board-of-regents/BOR_bistro.webp')"
                alt="The Bulldog Bistro at the Georgia Center"
                loading="lazy"
              />
            </figure>
            <p>
              Complimentary hot breakfast will be available to all guests at the Bulldog
              Bistro, located on the first floor of the Georgia Center.
            </p>
          </li>
          <li class="bor-event">
            <p class="bor-event__title">
              <span class="bor-event__time">10-11 a.m.</span>
              <span class="bor-event__name">
                Tour of the Medical Education and Research Building, School of Medicine
              </span>
            </p>
            <ul class="bor-event__details">
              <li>
                <strong>Location: <a :href="links.healthSciences" v-bind="externalLink">UGA Health Sciences Campus</a></strong>
              </li>
              <li>
                <strong>Shuttles: Shuttles will depart the Georgia Center at 9:30 a.m.</strong> Shuttles
                will return to the Georgia Center upon completion of the tour at 11 a.m.
              </li>
            </ul>
            <figure style="margin-top: 1rem;" class="bor-event__figure">
              <img
                :src="require('@/assets/img/board-of-regents/BOR_med-school.webp')"
                alt="The Medical Education and Research Building on the UGA Health Sciences Campus"
                loading="lazy"
              />
            </figure>
            <p>
              Get an exclusive look at the future of healthcare education at the University of
              Georgia during a guided tour of the new School of Medicine Medical Education and Research Building,
              currently under construction on the UGA Health Sciences Campus. Guests will also
              see the future site of the Victoria Kay Ivester School of Nursing, gaining
              insight into UGA&rsquo;s continued investment in educating the next generation of
              healthcare professionals and advancing research that will benefit communities
              across Georgia.
            </p>
          </li>
          <li class="bor-event">
            <p class="bor-event__title">
              <span class="bor-event__time">11:30 a.m.-1 p.m.</span>
              <span class="bor-event__name">Lunch</span>
            </p>
            <p>
              A lunch buffet will be available in the Georgia Center&rsquo;s Magnolia Ballroom
              beginning at 11:30 a.m.
            </p>
          </li>
          <li class="bor-event">
            <p class="bor-event__title">
              <span class="bor-event__time"
                >1-5 p.m.<span aria-hidden="true">*</span></span
              >
              <span class="bor-event__name">USG Board of Regents Meeting</span>
            </p>
            <p class="bor-event__note">
              *Official meeting times are pending final planning by the Board of Regents staff.
            </p>
            <ul class="bor-event__details">
              <li>
                <strong>Group Photo:</strong> Prior to the start of Tuesday&rsquo;s board
                meeting, the Regents will assemble for a group photo by the Conference
                Registration Desk on the first floor. This will take place at 12:45 p.m.
              </li>
              <li>
                <strong>Parking:</strong> Parking is available in the
                <strong
                  ><a :href="links.southDeck" v-bind="externalLink"
                    >South Campus Parking Deck</a
                  ></strong
                >. On arrival, please tell the parking attendant that you are here for the USG
                Board of Regents Meeting, and you will be directed to a reserved space.
              </li>
            </ul>
          </li>
          <li class="bor-event">
            <p class="bor-event__title">
              <span class="bor-event__time">6-8 p.m.</span>
              <span class="bor-event__name">Reception and Dinner</span>
            </p>
            <ul class="bor-event__details">
              <li>
                <strong>Location:
                <a :href="links.westEndZone" v-bind="externalLink"
                  >West End Zone at Sanford Stadium</a
                ></strong>
              </li>
              <li>
                <strong>Shuttles:</strong> Continuous round-trip shuttles will begin departing
                the Georgia Center at 5:30 p.m.
              </li>
              <li>
                <strong>Parking:</strong> We recommend using the shuttles, but if you wish to
                drive your own vehicle, a parking pass for the
                <strong
                  ><a :href="links.tateDeck" v-bind="externalLink"
                    >Tate Center Parking Deck</a
                  ></strong
                >
                will be required. Please note that you would like to bring your own vehicle on
                your registration form or send us an email at
                <strong
                  ><a href="mailto:usgmeeting@uga.edu">usgmeeting@uga.edu</a></strong
                > for more information.
              </li>
            </ul>
            <figure style="margin-top: 1rem !important" class="bor-event__figure">
              <img
                :src="require('@/assets/img/board-of-regents/BOR_sanford.webp')"
                alt="Sanford Stadium, home of the Georgia Bulldogs"
                loading="lazy"
              />
            </figure>
            <p>
              Celebrate the tradition and excellence of Georgia Athletics with an exclusive
              evening at Sanford Stadium. The experience will begin with on-field photo
              opportunities alongside Hairy Dawg with musical entertainment provided by the UGA
              Derbies pep band, followed by a private tour of the Georgia Bulldogs&rsquo; home
              locker room. Guests will then enjoy a reception and dinner in the West End Zone.
              Special remarks will be given by J. Reid Parker Director of Athletics Josh Brooks
              highlighting the powerful connection between academic and athletic excellence,
              while several UGA coaches and administrators join guests throughout the event.
            </p>
          </li>
        </ul>

        <!-- Wednesday -->
        <h3 class="bor-schedule__day">Wednesday, October 7</h3>
        <ul class="bor-schedule__events">
          <li class="bor-event">
            <p class="bor-event__title">
              <span class="bor-event__time">7-10 a.m.</span>
              <span class="bor-event__name">Breakfast</span>
            </p>
            <p>
              Complimentary hot breakfast will be available to all guests at the Bulldog
              Bistro, located in the Georgia Center.
            </p>
          </li>
          <li class="bor-event">
            <p class="bor-event__title">
              <span class="bor-event__time"
                >9 a.m.-Noon<span aria-hidden="true">*</span></span
              >
              <span class="bor-event__name">USG Board of Regents Meeting</span>
            </p>
            <p class="bor-event__note">
              *Official meeting times are pending final planning by the Board of Regents staff.
            </p>
            <ul class="bor-event__details">
              <li>
                <strong>Location:
                <a :href="links.georgiaCenter" v-bind="externalLink"
                  > Georgia Center for Continuing Education &amp; Hotel</a
                >, Mahler Hall
                </strong>
              </li>
              <li>
                Boxed lunches will be available for all guests at the conclusion of the USG
                Board of Regents Meeting.
              </li>
              <li>
                <strong>Parking:</strong> Parking is available in the
                <strong
                  ><a :href="links.southDeck" v-bind="externalLink"
                    >South Campus Parking Deck</a
                  ></strong
                >. On arrival, please tell the parking attendant that you are here for the USG
                Board of Regents Meeting, and you will be directed to a reserved space.
              </li>
            </ul>
          </li>
        </ul>
      </div>
    </section>

    <!-- Lodging and Meeting Location -->
    <section id="lodging" class="bor-content-section bor-jump-target">
      <Section
        type="inset"
        imgSide="left"
        imgURL="board-of-regents/BOR_lodging.jpg"
        altText="Georgia Center for Continuing Education and Hotel"
      >
        <h3>Lodging and Meeting Location</h3>

        <h4 class="bor-subheading">Lodging: Georgia Center for Continuing Education &amp; Hotel</h4>
        <ul class="bor-list">
          <li>
            <strong
              ><a :href="links.georgiaCenterAddress" v-bind="externalLink"
                >1197 South Lumpkin Street, Athens, GA 30602-3603</a
              ></strong
            >
          </li>
          <li>
            <strong
              ><a
                href="https://www.georgiacenter.uga.edu"
                class="bor-link"
                target="_blank"
                rel="noopener noreferrer"
              >www.georgiacenter.uga.edu</a
            ></strong>
          </li>
        </ul>

        <h4 class="bor-subheading">
          Meeting: Georgia Center for Continuing Education &amp; Hotel, Mahler Hall
        </h4>
        <ul class="bor-list">
          <li>
            <strong>Parking:</strong> Parking is available in the
            <strong
              ><a :href="links.southDeck" v-bind="externalLink">South Campus Parking Deck</a
              ></strong
            >. On
            arrival, please tell the parking attendant that you are here for the USG Board of Regents Meeting,
            and you will be directed to a reserved space.
          </li>
        </ul>
      </Section>
    </section>

    <!-- Parking Information -->
    <section id="parking" class="bor-content-section bor-jump-target">
      <Section
        type="inset"
        imgSide="left"
        imgURL="board-of-regents/BOR_map.jpg"
        altText="Campus parking map near the Georgia Center"
      >
        <h3>Parking Information</h3>
        <ul class="bor-list">
          <li>
            <strong>Meeting and Hotel Parking:</strong> Parking is available in the
            <strong
              ><a :href="links.southDeck" v-bind="externalLink">South Campus Parking Deck</a
              ></strong
            >. On
            arrival, please tell the parking attendant that you are here for the USG Board of Regents Meeting,
            and you will be directed to a reserved space.
          </li>
          <li>
            <strong
              ><a :href="links.botanicalGarden" v-bind="externalLink"
                >State Botanical Garden of Georgia</a
              >
              at the University of Georgia:</strong
            >
            On-site self-parking is available.
          </li>
          <li>
            <strong>Sanford Stadium Dinner:</strong> Personal vehicles may be parked at the
            <strong><a :href="links.tateDeck" v-bind="externalLink">Tate Parking Deck</a></strong>, but a parking
            pass will be required. Please note that you would like to bring your own vehicle on
            your registration form or send us an email at
            <strong><a href="mailto:usgmeeting@uga.edu">usgmeeting@uga.edu</a></strong> for more information.
          </li>
        </ul>
      </Section>
    </section>

    <!-- Shuttle Services -->
    <section id="shuttle-services" class="bor-content-section bor-jump-target">
      <Section
        type="inset"
        imgSide="left"
        imgURL="board-of-regents/BOR_bus.webp"
        altText="A UGA bus at the Georgia Center"
      >
        <h3>Shuttle Services</h3>

        <h4 class="bor-subheading">Monday, October 5</h4>
        <ul class="bor-shuttle-list">
          <li>
            Dinner at the <strong>State Botanical Garden</strong> at the University of Georgia, 6 p.m.
            <ul>
              <li>
                5:30 p.m. Continuous round-trip shuttle service begins from the Georgia Center.
              </li>
            </ul>
          </li>
        </ul>

        <h4 class="bor-subheading">Tuesday, October 6</h4>
        <ul class="bor-shuttle-list">
          <li>
            Tour of the <strong>Medical Education and Research Building</strong>, UGA Health Sciences Campus
            <ul>
              <li>9:30 a.m. Shuttles depart the Georgia Center.</li>
              <li>11 a.m. Shuttles return to the Georgia Center.</li>
            </ul>
          </li>
          <li>
            Dinner at <strong>Sanford Stadium</strong>
            <ul>
              <li>
                5:30 p.m. Continuous round-trip shuttle service begins from the Georgia Center.
              </li>
            </ul>
          </li>
        </ul>
      </Section>
    </section>

    <!-- Additional Resources -->
    <section id="additional-resources" class="bor-content-section bor-jump-target">
      <Section type="text">
        <h3>Additional Resources</h3>
        <div class="row g-3 mt-2">
          <div
            class="col-6 col-md-3"
            v-for="resource in resources"
            :key="resource.url"
          >
            <a
              :href="resource.url"
              class="bor-resource-card h-100"
              target="_blank"
              rel="noopener noreferrer"
            >
              <img
                v-if="resource.logoSrc"
                :src="resource.logoSrc"
                :alt="resource.logoAlt"
                class="bor-resource-card__logo"
              />
              <span v-else class="bor-resource-card__label">{{ resource.label }}</span>
              <span class="bor-resource-card__url">{{ resource.urlDisplay }}</span>
            </a>
          </div>
        </div>
      </Section>
    </section>

    <!-- Questions Banner -->
    <InfoBanner backgroundColor="light" textColor="black">
      <p class="text-center bor-questions my-0 py-4">
        <strong class="bor-questions__label">QUESTIONS?</strong>
        Email <strong><a href="mailto:usgmeeting@uga.edu">usgmeeting@uga.edu</a></strong> or call <strong><a tel=17065427619>706-542-7619</a></strong>
      </p>
    </InfoBanner>
  </main>
</template>

<script>
import Breadcrumb from "@/components/Breadcrumb.vue";
import PageHeader from "@/components/PageHeader.vue";
import Section from "@/components/Section.vue";
import InfoBanner from "@/components/InfoBanner.vue";

export default {
  name: "Board-of-Regents",
  metaInfo: {
    title: "Board of Regents Meeting | University of Georgia",
    description:
      "University System of Georgia Board of Regents Off-Site Meeting at the University of Georgia, October 5–7, 2026.",
    og: {
      image: "",
      title: "Board of Regents Meeting | University of Georgia",
      description:
        "University System of Georgia Board of Regents Off-Site Meeting at the University of Georgia.",
    },
  },
  data() {
    return {
      // Applied to every off-site link so the attributes stay consistent.
      externalLink: { target: "_blank", rel: "noopener noreferrer" },
      // Drives the Information jump links. Each id matches the id on the
      // corresponding .bor-jump-target section below.
      sections: [
        { id: "schedule-of-events", label: "Schedule of Events" },
        { id: "lodging", label: "Lodging and Meeting Location" },
        { id: "parking", label: "Parking Information" },
        { id: "shuttle-services", label: "Shuttle Services" },
        { id: "additional-resources", label: "Additional Resources" },
      ],
      // Map links supplied in BOR_content.docx. Held here rather than inline
      // because the Google Maps place URLs are too long to read in the template.
      links: {
        georgiaCenter:
          "https://www.google.com/maps/place/University+of+Georgia+Center+for+Continuing+Education+%26+Hotel/@33.96361,-83.3683456,15z/data=!4m9!3m8!1s0x88f66d1d80c262b3:0xfee796a04e1be2e8!5m2!4m1!1i2!8m2!3d33.9447632!4d-83.3777637!16s%2Fg%2F1tnhz56q",
        georgiaCenterAddress:
          "https://www.google.com/maps/dir/University+of+Georgia+Center+for+Continuing+Education+%26+Hotel,+1197+S+Lumpkin+St,+Athens,+GA+30602/data=!4m6!4m5!1m1!4e2!1m2!1m1!1s0x88f66d1d80c262b3:0xfee796a04e1be2e8",
        botanicalGarden:
          "https://www.google.com/maps/place/State+Botanical+Garden+of+Georgia+Visitor+Center/@33.9013,-83.3860276,17z/data=!3m1!4b1!4m6!3m5!1s0x88f66dcc3aa0f36f:0x3a48b5064c736187!8m2!3d33.9013!4d-83.3834527!16s%2Fg%2F11b7q7vt29",
        southDeck:
          "https://www.google.com/maps/place/South+Deck+Parking+Lot/@33.9456017,-83.3794946,17z/data=!3m1!4b1!4m6!3m5!1s0x88f66ce242e5f1e7:0xf2952221d479f051!8m2!3d33.9456017!4d-83.3769197!16s%2Fg%2F1ptwrcy6_",
        tateDeck:
          "https://www.google.com/maps/place/Tate+Center+Deck/@33.9456017,-83.3794946,17z/data=!4m6!3m5!1s0x88f66ce056e7ce07:0xd0f1b337acf20391!8m2!3d33.950614!4d-83.3765183!16s%2Fg%2F11f5by8m6k",
        healthSciences:
          "https://www.google.com/maps/place/Tate+Center+Deck/@33.9456017,-83.3794946,17z/data=!4m6!3m5!1s0x88f66ce056e7ce07:0xd0f1b337acf20391!8m2!3d33.950614!4d-83.3765183!16s%2Fg%2F11f5by8m6k",
        westEndZone: "https://maps.app.goo.gl/FWC8FBdFskwNJ5m37",
      },
      resources: [
        {
          label: "UGA Website",
          urlDisplay: "uga.edu",
          url: "https://uga.edu",
          logoSrc: require("@/assets/img/board-of-regents/UGAlogo_TopShield_1C_MARCM.png"),
          logoAlt: "UGA Logo",
        },
        {
          label: "UGA Calendar",
          urlDisplay: "calendar.uga.edu",
          url: "https://calendar.uga.edu",
          logoSrc: require("@/assets/img/board-of-regents/icon_calendar.png"),
          logoAlt: "UGA Calendar",
        },
        {
          label: "UGA Today",
          urlDisplay: "news.uga.edu",
          url: "https://news.uga.edu",
          logoSrc: require("@/assets/img/board-of-regents/icon_UGAToday.png"),
          logoAlt: "UGA Today",
        },
        {
          label: "Visit Athens GA",
          urlDisplay: "visitathensga.com",
          url: "https://www.visitathensga.com",
          logoSrc: require("@/assets/img/board-of-regents/icon_VisitAthens.png"),
          logoAlt: "Visit Athens GA",
        },
      ],
    };
  },
  components: {
    Breadcrumb,
    PageHeader,
    Section,
    InfoBanner,
  },
  mounted() {
    // The router's scrollBehavior always returns to the top, so a shared or
    // reloaded deep link (…/board-of-regents#parking) lands on the hero. Honor
    // the hash here instead, after the view has painted.
    const id = window.location.hash.slice(1);
    if (id) {
      this.$nextTick(() => this.jumpTo(id, "auto"));
    }
  },
  methods: {
    scrollToSection(event, id) {
      if (!document.getElementById(id)) return;
      event.preventDefault();
      this.jumpTo(id);
      // Keep the section linkable without routing: replaceState updates the
      // address bar but does not fire popstate, so Vue Router stays out of it.
      window.history.replaceState(null, "", `#${id}`);
    },
    jumpTo(id, behavior) {
      const target = document.getElementById(id);
      if (!target) return;
      const reduceMotion = window.matchMedia(
        "(prefers-reduced-motion: reduce)"
      ).matches;
      target.scrollIntoView({
        behavior: behavior || (reduceMotion ? "auto" : "smooth"),
        block: "start",
      });
      // Send keyboard and screen reader users along with the scroll.
      target.setAttribute("tabindex", "-1");
      target.focus({ preventScroll: true });
    },
  },
};
</script>

<style scoped lang="scss">
// The h1 text provides the accessible page name for screen readers;
// the logo image is the visual equivalent, so the text is hidden visually only.
:deep(.page-header__title) {
  @include visually-hidden();
}

.bor-hero__logo {
  display: block;
  width: 100%;
  max-width: 480px;
  margin: 0 auto;
}

.bor-schedule {
  background-color: tint-color($creamery, 70%);
  border: 1px solid tint-color($stegeman, 80%);
  padding: 2rem 1.5rem;
  max-width: 900px;
  margin-right: auto;
  margin-left: auto;

  @include media-breakpoint-up(md) {
    padding: 3rem 4rem;
  }

  &__day {
    @include heading-base;
    font-size: $header-sm;
    padding-bottom: 0.5rem;
    margin-bottom: 1.5rem;
    border-bottom: 1px solid tint-color($stegeman, 60%);

    // Space each day off from the one above it.
    * + & {
      margin-top: 3rem;
    }
  }

  &__events {
    list-style: none;
    padding-left: 0;
    margin-bottom: 0;
  }
}

// Jump links to each section of the page.
.bor-jump {
  margin-top: 1.5rem;

  &__list {
    list-style: none;
    padding-left: 0;
    margin-bottom: 0;
    display: flex;
    flex-wrap: wrap;
    justify-content: center;
    gap: 0.75rem;

    // Full-width stacked buttons on phones, where the labels would otherwise
    // wrap into ragged one- and two-line pills.
    @include media-breakpoint-down(sm) {
      flex-direction: column;
    }
  }
}

// The navbar is sticky at 67px, so an unadjusted jump hides the section
// heading underneath it.
.bor-jump-target {
  scroll-margin-top: 5.5rem;

  // Focus is moved here programmatically to carry keyboard users along with
  // the scroll; the jump itself is the visual cue, so suppress the ring.
  &:focus {
    outline: none;
  }
}

// Width of the aligned time column in the schedule. Sized to the longest time
// string ("11:30 a.m. to 1 p.m.") set in condensed Oswald, plus its gutter.
$bor-time-width: 10rem;

.bor-event {
  font-family: $font-family-serif;

  & + & {
    margin-top: 2rem;
  }

  p {
    margin-bottom: 0.75rem;
  }

  // Align an event's body copy under its name rather than under its time.
  @include media-breakpoint-up(md) {
    p:not(.bor-event__title),
    .bor-event__figure,
    .bor-event__details {
      margin-left: $bor-time-width;
    }
  }

  &__figure {
    max-width: 32rem;
    margin: 0 0 1rem;

    img {
      display: block;
      width: 100%;
      height: auto;
    }
  }

  // Time and event name read as two distinct pieces: stacked on small screens,
  // and on larger screens the time sits in a fixed column so times line up
  // down the day, keeping the at-a-glance scan of the original schedule table.
  &__title {
    font-family: $font-family-sans-serif;
    font-weight: $font-weight-bold;
    display: flex;
    flex-direction: column;

    @include media-breakpoint-up(md) {
      flex-direction: row;
      align-items: baseline;
    }
  }

  &__time {
    // Quieter than the event name: Oswald at its lightest weight, in the warm
    // palette grey rather than red, so the name carries the emphasis.
    font-family: $font-family-sans-serif;
    letter-spacing: 0.02em;
    flex: 0 0 auto;

    @include media-breakpoint-up(md) {
      width: $bor-time-width;
      padding-right: 1rem;
      white-space: nowrap;
    }
  }

  &__name {
    flex: 1 1 auto;
    min-width: 0;
  }

  &__note {
    font-size: $text-sm;
    font-style: italic;
  }

  &__details {
    // Logistics read as sans throughout; the serif is reserved for the
    // narrative paragraphs that describe each event.
    font-family: $font-family-sans-serif;
    // These sit inside .bor-schedule__events, so the browser would default them
    // to a hollow circle. Force the disc used by the lists elsewhere on the page.
    list-style-type: disc;
    padding-left: 1.25rem;

    li {
      margin-bottom: 0.5rem;

      &:last-child {
        margin-bottom: 0;
      }
    }
  }
}

// Sub-headings within the Section-wrapped blocks (Lodging, Shuttle Services).
.bor-subheading {
  font-family: $font-family-sans-serif;
  font-weight: $font-weight-bold;
  font-size: $text-lg;
  margin-bottom: 0.75rem;

  * + & {
    margin-top: 1rem;
  }
}

.bor-list {
  padding-left: 1.25rem;
  margin-bottom: 1rem;

  li {
    margin-bottom: 0.5rem;
  }

  ul {
    // Nested lists would otherwise default to a hollow circle.
    list-style-type: disc;
    margin-top: 0.5rem;
  }
}

// Shuttle Services reads as an event line with its departure times beneath it.
.bor-shuttle-list {
  padding-left: 1.25rem;
  margin-bottom: 1rem;

  // Space each event group off from the one above it.
  > li + li {
    margin-top: 1rem;
  }

  ul {
    // Nested lists would otherwise default to a hollow circle.
    list-style-type: disc;
    padding-left: 1.25rem;
    margin-top: 0.5rem;

    li {
      margin-bottom: 0.5rem;

      &:last-child {
        margin-bottom: 0;
      }
    }
  }
}

.bor-content-section + .bor-content-section :deep(.section > *) {
  border-top: 1px solid $gray-200;
}

// Section sets its slotted <p> and <li> in serif, which would leave the body
// copy of Lodging, Parking and Shuttle Services in serif under their headings.
// Section's rule ties on specificity, so go through .section to outrank it.
.bor-content-section :deep(.section p),
.bor-content-section :deep(.section li) {
  font-family: $font-family-sans-serif;
}

// Section pads its images on all four sides (p-2, and p-lg-4 from lg up). On
// this page they should sit flush with the top of the column, so only the top
// is zeroed and the remaining sides keep Section's spacing. The Bootstrap
// padding utilities are !important, hence the !important here.
:deep(.section figure img) {
  padding-top: 0 !important;
}

.bor-resource-card {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  min-height: 120px;
  padding: 1.5rem 1rem;
  background-color: $red;
  color: $white;
  text-align: center;
  text-decoration: none;
  transition: background-color 0.2s ease;

  &:hover {
    background-color: darken($red, 8%);
    color: $white;
    text-decoration: none;
  }

  &__logo {
    display: block;
    max-width: 100%;
    max-height: 60px;
    object-fit: contain;
    margin-bottom: 0.4rem;
    filter: brightness(0) invert(1);
  }

  &__label {
    @include heading-base;
    font-size: $text-lg;
    margin-bottom: 0.4rem;
  }

  &__url {
    font-family: $font-family-sans-serif;
    font-size: $text-sm;
    opacity: 0.85;
  }
}

.bor-questions {
  font-size: $text-lg;
  font-family: $font-family-sans-serif;

  &__label {
    color: $red;
    font-size: $text-xl;
    font-family: $font-family-block;
    margin-right: 0.5rem;
  }

  a {
    color: $red;
  }
}
</style>
